import React, { useState, useEffect } from 'react'
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Chip,
  Grid,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Stack,
  Paper,
  Autocomplete,
  IconButton,
  Tooltip,
  Collapse,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material'
import {
  PlayArrow as RunIcon,
  ExpandMore as ExpandMoreIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Info as InfoIcon,
  Close as CloseIcon,
  TrackChanges as TrackIcon,
  Star as StarIcon,
  Code as CodeIcon,
  ContentCopy as CopyIcon,
  Layers as LayersIcon,
  ViewStream as MergedIcon,
} from '@mui/icons-material'
import { useSearchParams, useNavigate } from 'react-router-dom'
import { userAPI, compositionAPI, riskCodesAPI, tracksAPI, agentPromptsAPI } from '../services/api'
import { useAuth } from '../contexts/AuthContext'

// Helper to safely render values that might be objects
const renderValue = (value) => {
  if (value === null || value === undefined) return ''
  if (typeof value === 'object') return JSON.stringify(value, null, 2)
  return value
}

const ENGAGEMENT_TIERS = [
  { value: 'highly_engaged', label: 'Highly Engaged (80+)' },
  { value: 'engaged', label: 'Engaged (60-79)' },
  { value: 'moderate', label: 'Moderate (40-59)' },
  { value: 'low', label: 'Low (20-39)' },
  { value: 'at_risk', label: 'At Risk (<20)' },
]

const TIME_OF_DAY_OPTIONS = [
  { value: 'morning', label: 'Morning (6AM-12PM)' },
  { value: 'afternoon', label: 'Afternoon (12PM-6PM)' },
  { value: 'evening', label: 'Evening (6PM-10PM)' },
  { value: 'night', label: 'Night (10PM-6AM)' },
]

const INTERVENTION_PHASES = [
  { value: 'onboarding', label: 'Onboarding (Days 0-7)' },
  { value: 'early', label: 'Early (Days 8-30)' },
  { value: 'active', label: 'Active (Days 31-90)' },
  { value: 'maintenance', label: 'Maintenance (90+)' },
]

function SessionPreview() {
  const [searchParams, setSearchParams] = useSearchParams()
  const navigate = useNavigate()
  const { hasRole } = useAuth()
  const canViewAgentInstructions = hasRole('system_admin')
  const [users, setUsers] = useState([])
  const [riskCodes, setRiskCodes] = useState([])
  const [loading, setLoading] = useState(true)
  const [previewing, setPreviewing] = useState(false)
  const [error, setError] = useState(null)
  const [result, setResult] = useState(null)

  // Target track state (for track-focused mode)
  const targetTrackCode = searchParams.get('track')
  const [targetTrack, setTargetTrack] = useState(null)

  // Configuration state
  const [selectedUser, setSelectedUser] = useState(null)
  const [overrides, setOverrides] = useState({
    time_of_day: '',
    intervention_day: '',
    intervention_phase: '',
    engagement_tier: '',
    risk_factors: [],
  })

  // Agent instruction preview state
  const [promptPreviews, setPromptPreviews] = useState({}) // keyed by track_code
  const [loadingPrompts, setLoadingPrompts] = useState({}) // keyed by track_code
  const [expandedPrompts, setExpandedPrompts] = useState({}) // keyed by track_code
  const [promptViewMode, setPromptViewMode] = useState('merged') // 'merged' or 'layers'

  useEffect(() => {
    fetchData()
  }, [])

  // Fetch target track details when track param is present
  useEffect(() => {
    if (targetTrackCode) {
      fetchTargetTrack()
    } else {
      setTargetTrack(null)
    }
  }, [targetTrackCode])

  const fetchTargetTrack = async () => {
    try {
      // Find track by code
      const tracksRes = await tracksAPI.getAll({ search: targetTrackCode, includeInactive: 'true' })
      const track = (tracksRes.data || []).find((t) => t.code === targetTrackCode)
      if (track) {
        setTargetTrack(track)
      } else {
        setError(`Track "${targetTrackCode}" not found`)
      }
    } catch (err) {
      console.error('Error fetching target track:', err)
    }
  }

  const clearTargetTrack = () => {
    setSearchParams({})
    setTargetTrack(null)
  }

  const fetchData = async () => {
    try {
      setLoading(true)
      const [usersRes, riskCodesRes] = await Promise.all([
        userAPI.getUsers(),
        riskCodesAPI.getAll().catch(() => ({ data: [] })),
      ])
      setUsers(usersRes.data || [])
      setRiskCodes(riskCodesRes.data || [])
    } catch (err) {
      console.error('Error fetching data:', err)
      setError('Failed to load data.')
    } finally {
      setLoading(false)
    }
  }

  const handlePreview = async () => {
    if (!selectedUser) {
      setError('Please select a patient to preview.')
      return
    }

    try {
      setPreviewing(true)
      setError(null)
      setResult(null)

      // Build contextOverrides - only include non-empty values
      const contextOverrides = {}
      if (overrides.time_of_day) {
        contextOverrides.time_of_day = overrides.time_of_day
      }
      if (overrides.intervention_day) {
        contextOverrides.intervention_day = parseInt(overrides.intervention_day)
      }
      if (overrides.intervention_phase) {
        contextOverrides.intervention_phase = overrides.intervention_phase
      }
      if (overrides.engagement_tier) {
        contextOverrides.engagement_tier = overrides.engagement_tier
      }

      // Build riskFactorOverrides from risk_factors array. `value` is the
      // format-specific value (string for discrete, number for numeric, null
      // for presence). Backend falls back to severity for legacy callers.
      const riskFactorOverrides = overrides.risk_factors.map((rf) => ({
        code: rf.code,
        value: rf.value,
      }))

      const response = await compositionAPI.preview({
        userId: selectedUser.id,
        contextOverrides,
        riskFactorOverrides,
        targetTrackCode: targetTrackCode || undefined,
      })
      setResult(response.data)
    } catch (err) {
      console.error('Error previewing session:', err)
      setError(err.response?.data?.message || 'Failed to preview session. The composition API may not be available.')
      // Set mock result for development
      setResult({
        session_agenda: {
          id: 'preview-123',
          target_duration_minutes: 25,
          items: [
            {
              track_code: 'anxiety_psychoed',
              track_name: 'Anxiety Psychoeducation',
              track_type: 'education',
              domain: 'anxiety',
              estimated_duration_minutes: 15,
              priority_score: 128,
              priority_breakdown: {
                base_priority: 75,
                risk_alignment: 37,
                strategy_boost: 30,
                overdue_bonus: 0,
              },
              scheduling_state: { cadence: 'weekly', is_due: true, is_overdue: false },
              conditional_blocks_active: ['High Anxiety State'],
            },
            {
              track_code: 'grounding_skills',
              track_name: 'Grounding Skills',
              track_type: 'skill_building',
              domain: 'anxiety',
              estimated_duration_minutes: 10,
              priority_score: 142,
              priority_breakdown: {
                base_priority: 70,
                risk_alignment: 52,
                strategy_boost: 30,
                overdue_bonus: 0,
              },
              scheduling_state: { cadence: 'weekly', is_due: true, is_overdue: false },
            },
          ],
        },
        tracks_not_selected: [
          { code: 'sleep_hygiene', name: 'Sleep Hygiene', reason: 'Domain limit reached' },
          { code: 'advanced_cbt', name: 'Advanced CBT', reason: 'Prerequisite not met' },
        ],
        applied_strategies: [
          {
            name: 'High Risk Engagement',
            id: 1,
            conditions_matched: ['risk_factor_severity >= high', 'engagement_tier in [low, at_risk]'],
            actions: [
              { type: 'priority_boost', value: 30 },
              { type: 'limit_tracks_per_session', value: 2 },
              { type: 'set_tone', value: 'gentle' },
            ],
          },
        ],
        context_used: {
          intervention_day: 15,
          intervention_phase: 'early',
          engagement_score: 35,
          engagement_tier: 'low',
          time_of_day: 'morning',
          risk_factors: ['RISK_HIGH_ANXIETY'],
        },
      })
    } finally {
      setPreviewing(false)
    }
  }

  // Sensible default value for each format so the dropdown add doesn't
  // leave the factor in an ambiguous state.
  const defaultValueForRiskCode = (rc) => {
    if (!rc) return null
    if (rc.value_format === 'presence') return null
    if (rc.value_format === 'discrete') {
      const vals = Array.isArray(rc.discrete_values) ? rc.discrete_values : []
      return vals[0] ?? null
    }
    if (rc.value_format === 'numeric') {
      return rc.numeric_min != null ? Number(rc.numeric_min) : 0
    }
    return null
  }

  const addRiskFactor = (riskCode) => {
    if (!riskCode) return
    const rc = riskCodes.find((r) => r.code === riskCode)
    setOverrides((prev) => ({
      ...prev,
      risk_factors: [
        ...prev.risk_factors.filter((r) => r.code !== riskCode),
        { code: riskCode, value: defaultValueForRiskCode(rc) },
      ],
    }))
  }

  const updateRiskFactorValue = (riskCode, value) => {
    setOverrides((prev) => ({
      ...prev,
      risk_factors: prev.risk_factors.map((r) =>
        r.code === riskCode ? { ...r, value } : r
      ),
    }))
  }

  const removeRiskFactor = (riskCode) => {
    setOverrides((prev) => ({
      ...prev,
      risk_factors: prev.risk_factors.filter((r) => r.code !== riskCode),
    }))
  }

  // Fetch merged agent instructions for a track
  const fetchPromptPreview = async (trackCode) => {
    if (promptPreviews[trackCode]) {
      // Already loaded, just toggle visibility
      setExpandedPrompts((prev) => ({ ...prev, [trackCode]: !prev[trackCode] }))
      return
    }

    setLoadingPrompts((prev) => ({ ...prev, [trackCode]: true }))
    try {
      const contextOverrides = {
        time_of_day: overrides.time_of_day || result?.context_used?.time_of_day,
        intervention_day: overrides.intervention_day
          ? parseInt(overrides.intervention_day)
          : result?.context_used?.intervention_day,
        intervention_phase: overrides.intervention_phase || result?.context_used?.intervention_phase,
        engagement_score: result?.context_used?.engagement_score,
        risk_factors: overrides.risk_factors.length > 0
          ? overrides.risk_factors
          : result?.context_used?.risk_factors?.map((rf) => ({
              code: rf.risk_code || rf,
              value: rf.value ?? null,
            })) || [],
      }

      const response = await agentPromptsAPI.previewMerged({
        trackCode,
        userId: selectedUser?.id,
        contextOverrides,
      })

      setPromptPreviews((prev) => ({ ...prev, [trackCode]: response.data }))
      setExpandedPrompts((prev) => ({ ...prev, [trackCode]: true }))
    } catch (err) {
      console.error('Error fetching prompt preview:', err)
      setPromptPreviews((prev) => ({
        ...prev,
        [trackCode]: { error: err.response?.data?.error || 'Failed to load prompt preview' },
      }))
      setExpandedPrompts((prev) => ({ ...prev, [trackCode]: true }))
    } finally {
      setLoadingPrompts((prev) => ({ ...prev, [trackCode]: false }))
    }
  }

  // Copy prompt to clipboard
  const copyPromptToClipboard = (trackCode) => {
    const preview = promptPreviews[trackCode]
    if (preview?.merged_prompt) {
      navigator.clipboard.writeText(preview.merged_prompt)
    }
  }

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    )
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Session Preview
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Test how the composition engine builds sessions for different patient contexts.
            See exactly which pathways are selected and why.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<RunIcon />}
          onClick={handlePreview}
          disabled={previewing || !selectedUser}
          size="large"
        >
          {previewing ? 'Composing...' : 'Run Preview'}
        </Button>
      </Box>

      {/* Target Track Banner */}
      {targetTrack && (
        <Paper
          sx={{
            p: 2,
            mb: 3,
            bgcolor: 'primary.50',
            border: '1px solid',
            borderColor: 'primary.200',
          }}
        >
          <Box display="flex" alignItems="center" justifyContent="space-between">
            <Box display="flex" alignItems="center" gap={2}>
              <TrackIcon color="primary" />
              <Box>
                <Typography variant="subtitle1" fontWeight="medium">
                  Pathway-Focused Mode: {targetTrack.name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Testing how "{targetTrack.code}" behaves across different patient contexts
                </Typography>
              </Box>
            </Box>
            <Box display="flex" alignItems="center" gap={1}>
              <Stack direction="row" spacing={0.5}>
                <Chip label={targetTrack.track_type} size="small" />
                <Chip label={targetTrack.domain} size="small" variant="outlined" />
                <Chip label={`Priority: ${targetTrack.base_priority}`} size="small" color="primary" />
              </Stack>
              <Button size="small" onClick={clearTargetTrack} startIcon={<CloseIcon />}>
                Clear
              </Button>
            </Box>
          </Box>
        </Paper>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Configuration Panel */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Test Configuration
              </Typography>

              <Autocomplete
                options={users.filter((u) => u.user_type === 'patient')}
                getOptionLabel={(option) => `${option.name} (${option.id})`}
                value={selectedUser}
                onChange={(e, newValue) => setSelectedUser(newValue)}
                renderInput={(params) => (
                  <TextField {...params} label="Select Patient" margin="normal" required />
                )}
              />

              <Divider sx={{ my: 2 }}>
                <Chip label="Context Overrides" size="small" />
              </Divider>

              <Typography variant="body2" color="text.secondary" gutterBottom>
                Leave empty to use actual patient context
              </Typography>

              <FormControl fullWidth margin="normal" size="small">
                <InputLabel>Time of Day</InputLabel>
                <Select
                  value={overrides.time_of_day}
                  label="Time of Day"
                  onChange={(e) => setOverrides({ ...overrides, time_of_day: e.target.value })}
                >
                  <MenuItem value="">Current</MenuItem>
                  {TIME_OF_DAY_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label="Intervention Day"
                type="number"
                value={overrides.intervention_day}
                onChange={(e) => setOverrides({ ...overrides, intervention_day: e.target.value })}
                margin="normal"
                size="small"
                placeholder="e.g., 15"
              />

              <FormControl fullWidth margin="normal" size="small">
                <InputLabel>Intervention Phase</InputLabel>
                <Select
                  value={overrides.intervention_phase}
                  label="Intervention Phase"
                  onChange={(e) => setOverrides({ ...overrides, intervention_phase: e.target.value })}
                >
                  <MenuItem value="">Current</MenuItem>
                  {INTERVENTION_PHASES.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl fullWidth margin="normal" size="small">
                <InputLabel>Engagement Tier</InputLabel>
                <Select
                  value={overrides.engagement_tier}
                  label="Engagement Tier"
                  onChange={(e) => setOverrides({ ...overrides, engagement_tier: e.target.value })}
                >
                  <MenuItem value="">Current</MenuItem>
                  {ENGAGEMENT_TIERS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Divider sx={{ my: 2 }}>
                <Chip label="Risk Factor Injection" size="small" />
              </Divider>

              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                Added risk factors will be injected into the patient context for this preview,
                replacing any factors the patient currently has.
              </Typography>

              <Stack spacing={1} sx={{ mb: 2 }}>
                {overrides.risk_factors.map((rf) => {
                  const rc = riskCodes.find((r) => r.code === rf.code)
                  const format = rc?.value_format
                  return (
                    <Box
                      key={rf.code}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        p: 1,
                        bgcolor: 'action.hover',
                        borderRadius: 1,
                      }}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography variant="body2" noWrap>
                          {rc?.name || rf.code}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {rf.code}{format ? ` · ${format}` : ''}
                        </Typography>
                      </Box>

                      {format === 'presence' && (
                        <Chip label="present" size="small" sx={{ fontSize: '0.7rem' }} />
                      )}

                      {format === 'discrete' && (
                        <FormControl size="small" sx={{ minWidth: 120 }}>
                          <InputLabel>Value</InputLabel>
                          <Select
                            value={rf.value ?? ''}
                            label="Value"
                            onChange={(e) => updateRiskFactorValue(rf.code, e.target.value)}
                          >
                            {(rc?.discrete_values || []).map((v) => (
                              <MenuItem key={v} value={v}>
                                {v}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      )}

                      {format === 'numeric' && (
                        <TextField
                          size="small"
                          type="number"
                          label={`Value (${rc.numeric_min}–${rc.numeric_max})`}
                          value={rf.value ?? ''}
                          onChange={(e) =>
                            updateRiskFactorValue(rf.code, e.target.value === '' ? '' : Number(e.target.value))
                          }
                          inputProps={{ min: rc.numeric_min, max: rc.numeric_max }}
                          sx={{ minWidth: 140 }}
                        />
                      )}

                      {!format && (
                        <Typography variant="caption" color="warning.main">
                          unknown format
                        </Typography>
                      )}

                      <IconButton size="small" onClick={() => removeRiskFactor(rf.code)}>
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  )
                })}
              </Stack>

              <Stack direction="row" spacing={1}>
                <FormControl size="small" sx={{ flexGrow: 1 }}>
                  <InputLabel>Add Risk</InputLabel>
                  <Select
                    value=""
                    label="Add Risk"
                    onChange={(e) => addRiskFactor(e.target.value)}
                  >
                    {riskCodes
                      .filter((risk) => !overrides.risk_factors.some((rf) => rf.code === risk.code))
                      .map((risk) => (
                        <MenuItem key={risk.code} value={risk.code}>
                          {risk.name}
                          <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                            {risk.value_format}
                          </Typography>
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        {/* Results Panel */}
        <Grid item xs={12} md={8}>
          {!result ? (
            <Card>
              <CardContent>
                <Box textAlign="center" py={8}>
                  <InfoIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
                  <Typography color="text.secondary">
                    Select a patient and click "Run Preview" to see composition results
                  </Typography>
                </Box>
              </CardContent>
            </Card>
          ) : (
            <Stack spacing={2}>
              {/* Context Used */}
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Context Used
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Chip label={`Day ${result.context_used?.intervention_day}`} size="small" />
                    <Chip label={result.context_used?.intervention_phase} size="small" />
                    <Chip label={result.context_used?.time_of_day} size="small" />
                    <Chip
                      label={`Engagement: ${result.context_used?.engagement_score}`}
                      size="small"
                    />
                    {(result.context_used?.risk_factors || []).map((rf, idx) => {
                      const code = rf.risk_code || rf
                      const v = rf.value
                      const label = (v != null && v !== '') ? `${code}: ${v}` : code
                      return (
                        <Chip key={idx} label={label} size="small" color="error" variant="outlined" />
                      )
                    })}
                  </Stack>
                </CardContent>
              </Card>

              {/* Target Track Analysis */}
              {result.target_track_analysis && (
                <Card
                  sx={{
                    border: '2px solid',
                    borderColor: result.target_track_analysis.was_selected ? 'success.main' : 'warning.main',
                  }}
                >
                  <CardContent>
                    <Box display="flex" alignItems="center" gap={1} mb={2}>
                      <StarIcon color={result.target_track_analysis.was_selected ? 'success' : 'warning'} />
                      <Typography variant="h6">
                        Target Track Analysis: {result.target_track_analysis.name}
                      </Typography>
                      <Chip
                        label={result.target_track_analysis.was_selected ? 'SELECTED' : 'NOT SELECTED'}
                        color={result.target_track_analysis.was_selected ? 'success' : 'warning'}
                        size="small"
                      />
                    </Box>

                    {result.target_track_analysis.error ? (
                      <Alert severity="error">{result.target_track_analysis.error}</Alert>
                    ) : (
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={6}>
                          <Typography variant="subtitle2" gutterBottom>
                            Selection Status
                          </Typography>
                          <Stack spacing={1}>
                            <Box display="flex" alignItems="center" gap={1}>
                              {result.target_track_analysis.was_selected ? (
                                <CheckIcon color="success" fontSize="small" />
                              ) : (
                                <CancelIcon color="warning" fontSize="small" />
                              )}
                              <Typography variant="body2">
                                {result.target_track_analysis.selection_status === 'selected' &&
                                  `Selected at position #${result.target_track_analysis.position_in_agenda}`}
                                {result.target_track_analysis.selection_status === 'ineligible' &&
                                  `Ineligible: ${result.target_track_analysis.eligibility?.reason}`}
                                {result.target_track_analysis.selection_status === 'conflict' &&
                                  `Excluded: ${result.target_track_analysis.conflict_reason}`}
                                {result.target_track_analysis.selection_status === 'not_found' &&
                                  'Pathway not found in active pathways'}
                              </Typography>
                            </Box>
                            {result.target_track_analysis.final_priority_score && (
                              <Typography variant="body2">
                                Final Priority Score: <strong>{result.target_track_analysis.final_priority_score}</strong>
                              </Typography>
                            )}
                          </Stack>
                        </Grid>

                        <Grid item xs={12} md={6}>
                          {result.target_track_analysis.priority_breakdown && (
                            <>
                              <Typography variant="subtitle2" gutterBottom>
                                Priority Breakdown
                              </Typography>
                              <Stack spacing={0.5}>
                                <Typography variant="body2">
                                  Base: {result.target_track_analysis.priority_breakdown.base_priority}
                                </Typography>
                                {result.target_track_analysis.priority_breakdown.strategy_boost > 0 && (
                                  <Typography variant="body2" color="primary.main">
                                    + Composition rule delta: {result.target_track_analysis.priority_breakdown.strategy_boost}
                                  </Typography>
                                )}
                                {result.target_track_analysis.priority_breakdown.overdue_bonus > 0 && (
                                  <Typography variant="body2" color="warning.main">
                                    + Overdue (mandatory): {result.target_track_analysis.priority_breakdown.overdue_bonus}
                                  </Typography>
                                )}
                              </Stack>
                            </>
                          )}
                        </Grid>

                        {!result.target_track_analysis.eligibility?.is_eligible && (
                          <Grid item xs={12}>
                            <Alert severity="warning" sx={{ mt: 1 }}>
                              <Typography variant="body2">
                                <strong>Why not eligible:</strong> {result.target_track_analysis.eligibility?.reason}
                              </Typography>
                              {result.target_track_analysis.eligibility?.prerequisites?.length > 0 && (
                                <Typography variant="body2" sx={{ mt: 0.5 }}>
                                  Prerequisites: {result.target_track_analysis.eligibility.prerequisites.join(', ')}
                                </Typography>
                              )}
                            </Alert>
                          </Grid>
                        )}
                      </Grid>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Session Agenda */}
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Session Agenda
                  </Typography>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Target duration: {result.session_agenda?.target_duration_minutes} minutes
                  </Typography>

                  {(result.session_agenda?.items || []).map((item, index) => {
                    const isTargetTrack = targetTrackCode && item.track_code === targetTrackCode
                    return (
                    <Accordion
                      key={item.track_code}
                      defaultExpanded={isTargetTrack || index === 0}
                      sx={isTargetTrack ? {
                        border: '2px solid',
                        borderColor: 'success.main',
                        bgcolor: 'success.50',
                      } : {}}
                    >
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Stack direction="row" spacing={2} alignItems="center" sx={{ width: '100%', pr: 2 }}>
                          {isTargetTrack && <StarIcon color="success" fontSize="small" />}
                          <Typography variant="body1" fontWeight="medium">
                            {index + 1}. {item.track_name}
                          </Typography>
                          {isTargetTrack && (
                            <Chip label="TARGET" size="small" color="success" />
                          )}
                          <Chip label={item.track_type} size="small" />
                          <Chip label={item.domain} size="small" variant="outlined" />
                          <Box sx={{ flexGrow: 1 }} />
                          <Chip label={`${item.estimated_duration_minutes} min`} size="small" />
                          <Chip
                            label={`Score: ${item.priority_score}`}
                            size="small"
                            color="primary"
                          />
                        </Stack>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Paper sx={{ p: 2, bgcolor: 'grey.50' }}>
                          <Typography variant="subtitle2" gutterBottom>
                            Priority Breakdown:
                          </Typography>
                          <Stack spacing={0.5}>
                            <Typography variant="body2">
                              Base priority: {item.priority_breakdown?.base_priority}
                            </Typography>
                            {item.priority_breakdown?.strategy_boost !== 0 && (
                              <Typography variant="body2" color="primary.main">
                                + Composition rule delta: {item.priority_breakdown?.strategy_boost}
                              </Typography>
                            )}
                            {item.priority_breakdown?.overdue_bonus > 0 && (
                              <Typography variant="body2" color="warning.main">
                                + Overdue (mandatory): {item.priority_breakdown?.overdue_bonus}
                              </Typography>
                            )}
                            {item.scheduling_state && (
                              <Typography variant="body2" color="text.secondary">
                                Schedule: {item.scheduling_state.cadence}
                                {item.scheduling_state.is_overdue && ' (OVERDUE)'}
                              </Typography>
                            )}
                          </Stack>
                          {item.conditional_blocks_active?.length > 0 && (
                            <Box mt={2}>
                              <Typography variant="subtitle2" gutterBottom>
                                Active Conditional Blocks:
                              </Typography>
                              {item.conditional_blocks_active.map((block) => (
                                <Chip
                                  key={block}
                                  label={block}
                                  size="small"
                                  color="info"
                                  sx={{ mr: 0.5 }}
                                />
                              ))}
                            </Box>
                          )}

                          {/* Agent Instructions Preview — system_admin only */}
                          {canViewAgentInstructions && (
                          <>
                          <Divider sx={{ my: 2 }} />
                          <Box>
                            <Box display="flex" alignItems="center" gap={1}>
                              <Button
                                size="small"
                                startIcon={loadingPrompts[item.track_code] ? <CircularProgress size={16} /> : <CodeIcon />}
                                onClick={() => fetchPromptPreview(item.track_code)}
                                disabled={loadingPrompts[item.track_code]}
                              >
                                {expandedPrompts[item.track_code] ? 'Hide' : 'Show'} Agent Instructions
                              </Button>

                              {expandedPrompts[item.track_code] && promptPreviews[item.track_code] && !promptPreviews[item.track_code].error && (
                                <ToggleButtonGroup
                                  value={promptViewMode}
                                  exclusive
                                  onChange={(e, value) => value && setPromptViewMode(value)}
                                  size="small"
                                >
                                  <ToggleButton value="merged">
                                    <Tooltip title="Merged View">
                                      <MergedIcon fontSize="small" />
                                    </Tooltip>
                                  </ToggleButton>
                                  <ToggleButton value="layers">
                                    <Tooltip title="Layers View">
                                      <LayersIcon fontSize="small" />
                                    </Tooltip>
                                  </ToggleButton>
                                </ToggleButtonGroup>
                              )}
                            </Box>

                            <Collapse in={expandedPrompts[item.track_code]}>
                              {promptPreviews[item.track_code] && (
                                <Box mt={2}>
                                  {promptPreviews[item.track_code].error ? (
                                    <Alert severity="error">
                                      {promptPreviews[item.track_code].error}
                                    </Alert>
                                  ) : promptViewMode === 'merged' ? (
                                    <>
                                      {/* Source Attribution */}
                                      <Box mb={1}>
                                        <Typography variant="caption" color="text.secondary">
                                          SOURCES:
                                        </Typography>
                                        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                                          {promptPreviews[item.track_code].sources?.base_prompt && (
                                            <Chip
                                              label={`Base: ${promptPreviews[item.track_code].sources.base_prompt.agent_type}`}
                                              size="small"
                                              color="primary"
                                              variant="outlined"
                                            />
                                          )}
                                          <Chip
                                            label={`Track: ${promptPreviews[item.track_code].sources?.track?.code}`}
                                            size="small"
                                            color="secondary"
                                            variant="outlined"
                                          />
                                          {promptPreviews[item.track_code].sources?.applied_strategies?.map((s) => (
                                            <Chip
                                              key={s.id}
                                              label={`Composition Rule: ${s.name}`}
                                              size="small"
                                              color="warning"
                                              variant="outlined"
                                            />
                                          ))}
                                          {promptPreviews[item.track_code].sources?.strategy_tone && (
                                            <Chip
                                              label={`Tone: ${promptPreviews[item.track_code].sources.strategy_tone}`}
                                              size="small"
                                              color="info"
                                            />
                                          )}
                                        </Stack>
                                      </Box>

                                      {/* Merged Prompt */}
                                      <Paper
                                        sx={{
                                          p: 2,
                                          bgcolor: 'grey.900',
                                          color: 'grey.100',
                                          maxHeight: 400,
                                          overflow: 'auto',
                                          position: 'relative',
                                        }}
                                      >
                                        <Tooltip title="Copy to clipboard">
                                          <IconButton
                                            size="small"
                                            onClick={() => copyPromptToClipboard(item.track_code)}
                                            sx={{
                                              position: 'absolute',
                                              top: 8,
                                              right: 8,
                                              color: 'grey.400',
                                              '&:hover': { color: 'grey.100' },
                                            }}
                                          >
                                            <CopyIcon fontSize="small" />
                                          </IconButton>
                                        </Tooltip>
                                        <Typography
                                          variant="body2"
                                          component="pre"
                                          sx={{
                                            fontFamily: 'monospace',
                                            fontSize: '0.75rem',
                                            whiteSpace: 'pre-wrap',
                                            wordBreak: 'break-word',
                                            m: 0,
                                          }}
                                        >
                                          {promptPreviews[item.track_code].merged_prompt}
                                        </Typography>
                                      </Paper>
                                    </>
                                  ) : (
                                    /* Layers View */
                                    <Stack spacing={2}>
                                      {/* Base Layer */}
                                      {promptPreviews[item.track_code].layers?.base && (
                                        <Paper
                                          variant="outlined"
                                          sx={{
                                            p: 2,
                                            borderLeft: 4,
                                            borderColor: 'primary.main',
                                          }}
                                        >
                                          <Box display="flex" alignItems="center" gap={1} mb={1}>
                                            <Chip label="BASE PROMPT" size="small" color="primary" />
                                            <Typography variant="caption" color="text.secondary">
                                              Agent type: {promptPreviews[item.track_code].sources?.base_prompt?.agent_type}
                                            </Typography>
                                          </Box>
                                          <Stack spacing={1.5}>
                                            {promptPreviews[item.track_code].layers.base.persona && (
                                              <Box>
                                                <Typography variant="caption" color="text.secondary" fontWeight="medium">
                                                  Persona
                                                </Typography>
                                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', bgcolor: 'grey.50', p: 1, borderRadius: 1 }}>
                                                  {renderValue(promptPreviews[item.track_code].layers.base.persona)}
                                                </Typography>
                                              </Box>
                                            )}
                                            {promptPreviews[item.track_code].layers.base.base_instructions && (
                                              <Box>
                                                <Typography variant="caption" color="text.secondary" fontWeight="medium">
                                                  Base Instructions
                                                </Typography>
                                                <Typography
                                                  variant="body2"
                                                  component="pre"
                                                  sx={{
                                                    whiteSpace: 'pre-wrap',
                                                    bgcolor: 'grey.50',
                                                    p: 1,
                                                    borderRadius: 1,
                                                    fontFamily: 'monospace',
                                                    fontSize: '0.75rem',
                                                    maxHeight: 200,
                                                    overflow: 'auto',
                                                  }}
                                                >
                                                  {renderValue(promptPreviews[item.track_code].layers.base.base_instructions)}
                                                </Typography>
                                              </Box>
                                            )}
                                            <Box display="flex" gap={2}>
                                              <Box>
                                                <Typography variant="caption" color="text.secondary">Tone</Typography>
                                                <Chip label={promptPreviews[item.track_code].layers.base.default_tone || 'warm'} size="small" sx={{ ml: 1 }} />
                                              </Box>
                                              <Box>
                                                <Typography variant="caption" color="text.secondary">Approach</Typography>
                                                <Chip label={promptPreviews[item.track_code].layers.base.default_approach || 'supportive'} size="small" sx={{ ml: 1 }} />
                                              </Box>
                                            </Box>
                                          </Stack>
                                        </Paper>
                                      )}

                                      {/* Track Layer */}
                                      {promptPreviews[item.track_code].layers?.track && (
                                        <Paper
                                          variant="outlined"
                                          sx={{
                                            p: 2,
                                            borderLeft: 4,
                                            borderColor: 'secondary.main',
                                          }}
                                        >
                                          <Box display="flex" alignItems="center" gap={1} mb={1}>
                                            <Chip label="TRACK LAYER" size="small" color="secondary" />
                                            <Typography variant="caption" color="text.secondary">
                                              {promptPreviews[item.track_code].sources?.track?.code}
                                            </Typography>
                                          </Box>
                                          <Stack spacing={1.5}>
                                            {promptPreviews[item.track_code].layers.track.context_preamble && (
                                              <Box>
                                                <Typography variant="caption" color="text.secondary" fontWeight="medium">
                                                  Context Preamble
                                                </Typography>
                                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', bgcolor: 'secondary.50', p: 1, borderRadius: 1 }}>
                                                  {renderValue(promptPreviews[item.track_code].layers.track.context_preamble)}
                                                </Typography>
                                              </Box>
                                            )}
                                            {promptPreviews[item.track_code].layers.track.approach && (
                                              <Box>
                                                <Typography variant="caption" color="text.secondary" fontWeight="medium">
                                                  Approach Override
                                                </Typography>
                                                <Chip label={renderValue(promptPreviews[item.track_code].layers.track.approach)} size="small" color="secondary" variant="outlined" />
                                              </Box>
                                            )}
                                            {promptPreviews[item.track_code].layers.track.tone && (
                                              <Box>
                                                <Typography variant="caption" color="text.secondary" fontWeight="medium">
                                                  Tone Override
                                                </Typography>
                                                <Chip label={renderValue(promptPreviews[item.track_code].layers.track.tone)} size="small" color="secondary" variant="outlined" />
                                              </Box>
                                            )}
                                            {promptPreviews[item.track_code].layers.track.response_guidance && (
                                              <Box>
                                                <Typography variant="caption" color="text.secondary" fontWeight="medium">
                                                  Response Guidance
                                                </Typography>
                                                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', bgcolor: 'secondary.50', p: 1, borderRadius: 1 }}>
                                                  {renderValue(promptPreviews[item.track_code].layers.track.response_guidance)}
                                                </Typography>
                                              </Box>
                                            )}
                                            {promptPreviews[item.track_code].layers.track.conditional_blocks_active?.length > 0 && (
                                              <Box>
                                                <Typography variant="caption" color="text.secondary" fontWeight="medium">
                                                  Active Conditional Blocks ({promptPreviews[item.track_code].layers.track.conditional_blocks_active.length} of {promptPreviews[item.track_code].layers.track.conditional_blocks_total})
                                                </Typography>
                                                <Stack spacing={1} mt={0.5}>
                                                  {promptPreviews[item.track_code].layers.track.conditional_blocks_active.map((block, idx) => (
                                                    <Paper key={idx} sx={{ p: 1, bgcolor: 'info.50' }}>
                                                      <Typography variant="body2" fontWeight="medium">{block.name}</Typography>
                                                      <Typography variant="caption" color="text.secondary">
                                                        Conditions: {JSON.stringify(block.conditions)}
                                                      </Typography>
                                                      {block.extend && Object.keys(block.extend).length > 0 && (
                                                        <Box mt={0.5}>
                                                          {Object.entries(block.extend).map(([key, value]) => (
                                                            <Typography key={key} variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>
                                                              +{key}: {typeof value === 'string' ? value : JSON.stringify(value)}
                                                            </Typography>
                                                          ))}
                                                        </Box>
                                                      )}
                                                    </Paper>
                                                  ))}
                                                </Stack>
                                              </Box>
                                            )}
                                          </Stack>
                                        </Paper>
                                      )}

                                      {/* Composition Rule Layer */}
                                      {(promptPreviews[item.track_code].layers?.strategies?.applied_count > 0 ||
                                        promptPreviews[item.track_code].layers?.strategies?.tone_override) && (
                                        <Paper
                                          variant="outlined"
                                          sx={{
                                            p: 2,
                                            borderLeft: 4,
                                            borderColor: 'warning.main',
                                          }}
                                        >
                                          <Box display="flex" alignItems="center" gap={1} mb={1}>
                                            <Chip label="COMPOSITION RULE LAYER" size="small" color="warning" />
                                            <Typography variant="caption" color="text.secondary">
                                              {promptPreviews[item.track_code].layers?.strategies?.applied_count} composition rule{promptPreviews[item.track_code].layers?.strategies?.applied_count !== 1 ? 's' : ''} applied
                                            </Typography>
                                          </Box>
                                          <Stack spacing={1.5}>
                                            {promptPreviews[item.track_code].layers?.strategies?.tone_override && (
                                              <Box>
                                                <Typography variant="caption" color="text.secondary" fontWeight="medium">
                                                  Tone Override
                                                </Typography>
                                                <Chip
                                                  label={promptPreviews[item.track_code].layers.strategies.tone_override}
                                                  size="small"
                                                  color="warning"
                                                />
                                              </Box>
                                            )}
                                            {promptPreviews[item.track_code].layers?.strategies?.applied_names?.length > 0 && (
                                              <Box>
                                                <Typography variant="caption" color="text.secondary" fontWeight="medium">
                                                  Applied Composition Rules
                                                </Typography>
                                                <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mt: 0.5 }}>
                                                  {promptPreviews[item.track_code].layers.strategies.applied_names.map((name) => (
                                                    <Chip key={name} label={name} size="small" color="warning" variant="outlined" />
                                                  ))}
                                                </Stack>
                                              </Box>
                                            )}
                                            {promptPreviews[item.track_code].layers?.strategies?.prompt_modifications?.length > 0 && (
                                              <Box>
                                                <Typography variant="caption" color="text.secondary" fontWeight="medium">
                                                  Prompt Modifications
                                                </Typography>
                                                <Stack spacing={1} mt={0.5}>
                                                  {promptPreviews[item.track_code].layers.strategies.prompt_modifications.map((mod, idx) => (
                                                    <Paper key={idx} sx={{ p: 1, bgcolor: 'warning.50' }}>
                                                      <Typography variant="body2" fontWeight="medium">{mod.strategy_name}</Typography>
                                                      {Object.entries(mod).filter(([k]) => k !== 'strategy_name').map(([key, value]) => (
                                                        <Typography key={key} variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.7rem' }}>
                                                          +{key}: {typeof value === 'string' ? value : JSON.stringify(value)}
                                                        </Typography>
                                                      ))}
                                                    </Paper>
                                                  ))}
                                                </Stack>
                                              </Box>
                                            )}
                                          </Stack>
                                        </Paper>
                                      )}

                                      {/* No modifications notice */}
                                      {!promptPreviews[item.track_code].layers?.track?.context_preamble &&
                                       !promptPreviews[item.track_code].layers?.track?.conditional_blocks_active?.length &&
                                       !promptPreviews[item.track_code].layers?.strategies?.applied_count && (
                                        <Alert severity="info">
                                          No pathway-specific or composition rule modifications are applied. The merged prompt uses only the base prompt.
                                        </Alert>
                                      )}
                                    </Stack>
                                  )}
                                </Box>
                              )}
                            </Collapse>
                          </Box>
                          </>
                          )}
                        </Paper>
                      </AccordionDetails>
                    </Accordion>
                  )})}

                </CardContent>
              </Card>

              {/* Excluded Tracks */}
              {result.tracks_not_selected?.length > 0 && (
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Pathways Not Selected
                    </Typography>
                    {result.tracks_not_selected.map((track) => {
                      const isTargetTrack = targetTrackCode && track.code === targetTrackCode
                      return (
                        <Box
                          key={track.code}
                          display="flex"
                          alignItems="center"
                          mb={1}
                          sx={isTargetTrack ? {
                            p: 1,
                            borderRadius: 1,
                            bgcolor: 'error.50',
                            border: '1px solid',
                            borderColor: 'error.200',
                          } : {}}
                        >
                          {isTargetTrack ? (
                            <StarIcon color="error" sx={{ mr: 1 }} />
                          ) : (
                            <CancelIcon color="disabled" sx={{ mr: 1 }} />
                          )}
                          <Typography variant="body2" fontWeight={isTargetTrack ? 'medium' : 'normal'}>
                            {track.name}
                          </Typography>
                          {isTargetTrack && (
                            <Chip label="TARGET" size="small" color="error" sx={{ ml: 1 }} />
                          )}
                          <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                            — {track.reason}
                          </Typography>
                        </Box>
                      )
                    })}
                  </CardContent>
                </Card>
              )}

              {/* Applied Composition Rules */}
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Composition Rule Evaluation
                  </Typography>
                  {(result.applied_strategies || []).length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No composition rules matched the current context
                    </Typography>
                  ) : (
                    result.applied_strategies.map((rule) => (
                      <Box key={rule.name || rule.id} display="flex" alignItems="start" mb={2}>
                        <CheckIcon color="success" sx={{ mr: 1, mt: 0.5 }} />
                        <Box>
                          <Typography variant="body2" fontWeight="medium">
                            {rule.name}
                          </Typography>
                          {rule.conditions_matched?.length > 0 && (
                            <Typography variant="body2" color="text.secondary">
                              Conditions: {rule.conditions_matched.join(', ')}
                            </Typography>
                          )}
                          {rule.actions?.length > 0 && (
                            <Typography variant="body2" color="primary.main">
                              Actions: {rule.actions.map((a) =>
                                typeof a === 'string' ? a : `${a.type}${a.value ? `: ${a.value}` : ''}`
                              ).join(', ')}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    ))
                  )}
                </CardContent>
              </Card>
            </Stack>
          )}
        </Grid>
      </Grid>
    </Box>
  )
}

export default SessionPreview
